import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  GripVertical,
  MoreVertical,
  ExternalLink,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { templateService } from "../services/templateService";
import { fieldService } from "../services/fieldService";
import { projectService } from "../services/projectService";
import { useAuth } from "../context/AuthContext";
import { getStoredClientId } from "../utils/tokenUtils";
import toast from "react-hot-toast";

// Default fields that cannot be removed
const DEFAULT_FIELDS = [
  "Title",
  "Choose Template",
  "Steps & Expected Results",
  "Requirements",
  "Attachments",
  "Owner",
];

const CreateTemplatePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const storedClientId = getStoredClientId();
  const urlClientId = searchParams.get("client_id");
  const templateId = searchParams.get("template_id"); // For edit mode
  const layoutType = searchParams.get("layout_type") || "test_case"; // Default to test_case
  const effectiveClientId = urlClientId || storedClientId || user?.client_id;
  const isEditMode = !!templateId;

  const [templateName, setTemplateName] = useState("");
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [applyToAllFuture, setApplyToAllFuture] = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [projects, setProjects] = useState([]);
  const [availableFields, setAvailableFields] = useState([]);
  const [descriptionFields, setDescriptionFields] = useState([]);
  const [propertiesFields, setPropertiesFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [selectedField, setSelectedField] = useState(null);
  const [draggedField, setDraggedField] = useState(null);

  useEffect(() => {
    if (effectiveClientId) {
      loadFields();
      loadProjects();
      if (isEditMode && templateId) {
        loadTemplate();
      } else {
        initializeDefaultFields();
      }
    }
  }, [effectiveClientId, templateId, isEditMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTemplate = async () => {
    if (!templateId || !effectiveClientId) return;

    try {
      setLoadingTemplate(true);
      const response = await templateService.getTemplateById(templateId);

      if (response.success && response.data?.template) {
        const template = response.data.template;

        // Set template name
        setTemplateName(template.name || "");

        // Set apply to all future projects
        setApplyToAllFuture(template.apply_to_all_future_projects || false);

        // Load fields from template
        if (template.fields && Array.isArray(template.fields)) {
          const descFields = template.fields
            .filter((f) => f.group_name === "description")
            .sort((a, b) => (a.position || 0) - (b.position || 0))
            .map((f) => ({
              field_id: f.field_id,
              name: f.field_name || f.name,
              type: f.field_type || f.type,
              group_name: "description",
              position: f.position || 0,
            }));

          const propFields = template.fields
            .filter((f) => f.group_name === "properties")
            .sort((a, b) => (a.position || 0) - (b.position || 0))
            .map((f) => ({
              field_id: f.field_id,
              name: f.field_name || f.name,
              type: f.field_type || f.type,
              group_name: "properties",
              position: f.position || 0,
            }));

          setDescriptionFields(descFields);
          setPropertiesFields(propFields);
        }

        // Load linked projects
        if (
          template.linked_projects &&
          Array.isArray(template.linked_projects)
        ) {
          setSelectedProjects(
            template.linked_projects.map((p) => p.project_id),
          );
        }
      } else {
        toast.error("Failed to load layout");
        navigate("/settings?tab=0");
      }
    } catch (error) {
      console.error("Error loading template:", error);
      toast.error("Failed to load layout");
      navigate("/settings?tab=0");
    } finally {
      setLoadingTemplate(false);
    }
  };

  const initializeDefaultFields = async () => {
    try {
      // Load all fields to find default ones
      const response = await fieldService.getFields({
        client_id: effectiveClientId,
      });
      if (response.success && response.data?.fields) {
        const allFields = response.data.fields;

        // Find default fields by name (case-insensitive)
        const defaultFieldList = DEFAULT_FIELDS.map((fieldName) => {
          return allFields.find(
            (f) =>
              f.name.toLowerCase() === fieldName.toLowerCase() ||
              f.name.toLowerCase().includes(fieldName.toLowerCase()),
          );
        }).filter(Boolean);

        // Initialize with default fields in description and properties
        // Title, Description, Preconditions, Steps & Expected Results, Attachments -> Description Fields
        // Choose Template, Owner, State, Priority, Type of Test Case, Automation Status, Tags, Requirements -> Properties Fields

        const descriptionDefaults = defaultFieldList.filter((f) => {
          const name = f.name.toLowerCase();
          return (
            name.includes("title") ||
            name.includes("description") ||
            name.includes("precondition") ||
            name.includes("steps") ||
            name.includes("attachments")
          );
        });

        const propertiesDefaults = defaultFieldList.filter((f) => {
          const name = f.name.toLowerCase();
          return (
            name.includes("template") ||
            name.includes("owner") ||
            name.includes("state") ||
            name.includes("priority") ||
            name.includes("type") ||
            name.includes("automation") ||
            name.includes("tags") ||
            name.includes("requirement")
          );
        });

        setDescriptionFields(
          descriptionDefaults.map((f, i) => ({
            ...f,
            position: i,
            group_name: "description",
          })),
        );
        setPropertiesFields(
          propertiesDefaults.map((f, i) => ({
            ...f,
            position: i,
            group_name: "properties",
          })),
        );
      }
    } catch (error) {
      console.error("Error initializing default fields:", error);
    }
  };

  const loadFields = async () => {
    try {
      const response = await fieldService.getFields({
        client_id: effectiveClientId,
      });
      if (response.success && response.data?.fields) {
        setAvailableFields(response.data.fields);
      }
    } catch (error) {
      console.error("Error loading fields:", error);
      toast.error("Failed to load fields");
    }
  };

  const loadProjects = async () => {
    try {
      const response = await projectService.getProjects({
        client_id: effectiveClientId,
        page: 1,
        limit: 1000,
        is_active: true,
        is_deleted: false,
      });
      if (response.success && response.data?.projects) {
        setProjects(response.data.projects);
      }
    } catch (error) {
      console.error("Error loading projects:", error);
      toast.error("Failed to load projects");
    }
  };

  const handleAddField = (field) => {
    // Check if field is already added (more robust check)
    const isInDescription = descriptionFields.some(
      (f) =>
        f.field_id === field.field_id ||
        (f.field_id &&
          field.field_id &&
          f.field_id.toString() === field.field_id.toString()),
    );
    const isInProperties = propertiesFields.some(
      (f) =>
        f.field_id === field.field_id ||
        (f.field_id &&
          field.field_id &&
          f.field_id.toString() === field.field_id.toString()),
    );

    if (isInDescription || isInProperties) {
      toast.error(
        "This field is already added to the layout. Each field can only be used once.",
      );
      return;
    }

    // Add to description fields by default
    const newField = {
      ...field,
      position: descriptionFields.length,
      group_name: "description",
    };
    setDescriptionFields([...descriptionFields, newField]);
  };

  const handleRemoveField = (field) => {
    // Guard against null/undefined field
    if (!field) {
      console.error("handleRemoveField called with null/undefined field");
      return;
    }

    // Check if it's a default field
    const isDefault = DEFAULT_FIELDS.some(
      (defaultName) =>
        field.name &&
        field.name.toLowerCase().includes(defaultName.toLowerCase()),
    );

    if (isDefault) {
      toast.error("Default fields cannot be removed");
      return;
    }

    // Robust field comparison
    const compareField = (f1, f2) => {
      if (f1.field_id === f2.field_id) return true;
      if (
        f1.field_id &&
        f2.field_id &&
        f1.field_id.toString() === f2.field_id.toString()
      )
        return true;
      return false;
    };

    if (field.group_name === "description") {
      setDescriptionFields(
        descriptionFields
          .filter((f) => !compareField(f, field))
          .map((f, i) => ({ ...f, position: i })),
      );
      toast.success(`Field "${field.name}" removed`);
    } else {
      setPropertiesFields(
        propertiesFields
          .filter((f) => !compareField(f, field))
          .map((f, i) => ({ ...f, position: i })),
      );
      toast.success(`Field "${field.name}" removed`);
    }
  };

  const handleMoveField = (direction, field = null) => {
    // Use passed field or fallback to selectedField
    const targetField = field || selectedField;
    if (!targetField) {
      console.error("handleMoveField: No field provided");
      return;
    }

    const fields =
      targetField.group_name === "description"
        ? descriptionFields
        : propertiesFields;
    const setFields =
      targetField.group_name === "description"
        ? setDescriptionFields
        : setPropertiesFields;

    // Robust field comparison
    const compareField = (f1, f2) => {
      if (f1.field_id === f2.field_id) return true;
      if (
        f1.field_id &&
        f2.field_id &&
        f1.field_id.toString() === f2.field_id.toString()
      )
        return true;
      return false;
    };

    const index = fields.findIndex((f) => compareField(f, targetField));
    if (index === -1) {
      console.error("handleMoveField: Field not found in list");
      return;
    }

    const newFields = [...fields];

    if (direction === "up" && index > 0) {
      [newFields[index - 1], newFields[index]] = [
        newFields[index],
        newFields[index - 1],
      ];
      newFields[index - 1].position = index - 1;
      newFields[index].position = index;
      setFields(newFields);
    } else if (direction === "down" && index < newFields.length - 1) {
      [newFields[index], newFields[index + 1]] = [
        newFields[index + 1],
        newFields[index],
      ];
      newFields[index].position = index;
      newFields[index + 1].position = index + 1;
      setFields(newFields);
    } else if (direction === "top") {
      const movedField = newFields.splice(index, 1)[0];
      newFields.unshift(movedField);
      newFields.forEach((f, i) => {
        f.position = i;
      });
      setFields(newFields);
    } else if (direction === "bottom") {
      const movedField = newFields.splice(index, 1)[0];
      newFields.push(movedField);
      newFields.forEach((f, i) => {
        f.position = i;
      });
      setFields(newFields);
    }
  };

  const handleDragStart = (e, field) => {
    setDraggedField(field);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", ""); // Required for some browsers
    e.stopPropagation(); // Prevent event bubbling
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDragEnd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDraggedField(null);
  };

  const handleDrop = (e, targetGroup, targetIndex) => {
    e.preventDefault();
    e.stopPropagation();

    if (!draggedField) return;

    const sourceFields =
      draggedField.group_name === "description"
        ? descriptionFields
        : propertiesFields;
    const setSourceFields =
      draggedField.group_name === "description"
        ? setDescriptionFields
        : setPropertiesFields;
    const targetFields =
      targetGroup === "description" ? descriptionFields : propertiesFields;
    const setTargetFields =
      targetGroup === "description"
        ? setDescriptionFields
        : setPropertiesFields;

    // Robust field comparison
    const compareField = (f1, f2) => {
      if (f1.field_id === f2.field_id) return true;
      if (
        f1.field_id &&
        f2.field_id &&
        f1.field_id.toString() === f2.field_id.toString()
      )
        return true;
      return false;
    };

    // Check if field already exists in target group
    const existingIndex = targetFields.findIndex((f) =>
      compareField(f, draggedField),
    );

    // If moving within the same group, just reorder
    if (draggedField.group_name === targetGroup) {
      const newFields = [...targetFields];
      const sourceIndex = newFields.findIndex((f) =>
        compareField(f, draggedField),
      );

      if (sourceIndex !== -1 && sourceIndex !== targetIndex) {
        // Remove from old position
        const [movedField] = newFields.splice(sourceIndex, 1);
        // Insert at new position (adjust index if removing before target)
        const adjustedIndex =
          sourceIndex < targetIndex ? targetIndex - 1 : targetIndex;
        newFields.splice(
          Math.max(0, Math.min(adjustedIndex, newFields.length)),
          0,
          movedField,
        );
        // Update positions
        newFields.forEach((f, i) => {
          f.position = i;
        });
        setTargetFields(newFields);
      }
    } else {
      // Moving between groups
      // Remove from source
      const newSourceFields = sourceFields
        .filter((f) => !compareField(f, draggedField))
        .map((f, i) => ({ ...f, position: i }));

      // Add to target (only if not already there)
      let newTargetFields = [...targetFields];
      if (existingIndex === -1) {
        // Field doesn't exist in target, add it at the specified index
        const updatedField = { ...draggedField, group_name: targetGroup };
        // Ensure targetIndex is within bounds
        const insertIndex = Math.max(
          0,
          Math.min(targetIndex, newTargetFields.length),
        );
        newTargetFields.splice(insertIndex, 0, updatedField);
        newTargetFields.forEach((f, i) => {
          f.position = i;
        });
        setSourceFields(newSourceFields);
        setTargetFields(newTargetFields);
      } else {
        // Field already exists in target, just show a message
        toast.error("This field is already in the target group");
      }
    }

    setDraggedField(null);
  };

  const handleProjectToggle = (projectId) => {
    setSelectedProjects((prev) =>
      prev.includes(projectId)
        ? prev.filter((id) => id !== projectId)
        : [...prev, projectId],
    );
  };

  const handleCreateTemplate = async () => {
    if (!templateName.trim()) {
      toast.error("Layout name is required");
      return;
    }

    try {
      setLoading(true);

      // Prepare fields data
      const allFields = [
        ...descriptionFields.map((f, i) => ({
          field_id: f.field_id,
          group_name: "description",
          position: i,
          is_visible: true,
        })),
        ...propertiesFields.map((f, i) => ({
          field_id: f.field_id,
          group_name: "properties",
          position: i,
          is_visible: true,
        })),
      ];

      if (isEditMode && templateId) {
        // Update existing template
        const updateData = {
          name: templateName.trim(),
          description: "",
          apply_to_all_future_projects: applyToAllFuture,
          fields: allFields,
          project_ids: selectedProjects,
          client_id: effectiveClientId,
        };

        const response = await templateService.updateTemplate(
          templateId,
          updateData,
        );

        if (response.success) {
          toast.success("Layout updated successfully");
          navigate("/settings?tab=0");
        } else {
          toast.error(response.message || "Failed to update layout");
        }
      } else {
        // Create new template
        const templateData = {
          name: templateName.trim(),
          description: "",
          type: "custom",
          layout_type: layoutType, // 'test_case' or 'test_run'
          status: "enabled",
          is_default: false,
          apply_to_all_future_projects: applyToAllFuture,
          fields: allFields,
          project_ids: selectedProjects,
          client_id: effectiveClientId,
        };

        const response = await templateService.createTemplate(templateData);

        if (response.success) {
          toast.success("Layout created successfully");
          navigate("/settings?tab=0");
        } else {
          toast.error(response.message || "Failed to create layout");
        }
      }
    } catch (error) {
      console.error(
        `Error ${isEditMode ? "updating" : "creating"} template:`,
        error,
      );
      toast.error(
        error.response?.data?.message ||
          `Failed to ${isEditMode ? "update" : "create"} layout`,
      );
    } finally {
      setLoading(false);
    }
  };

  const availableFieldsForAdd = availableFields.filter((field) => {
    // More robust comparison - handle both string and number field_ids
    const isInDescription = descriptionFields.some(
      (f) =>
        f.field_id === field.field_id ||
        (f.field_id &&
          field.field_id &&
          f.field_id.toString() === field.field_id.toString()),
    );
    const isInProperties = propertiesFields.some(
      (f) =>
        f.field_id === field.field_id ||
        (f.field_id &&
          field.field_id &&
          f.field_id.toString() === field.field_id.toString()),
    );
    return !isInDescription && !isInProperties;
  });

  return (
    <div className="space-y-6 pb-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate("/settings?tab=0")}
          className="p-2 text-text-secondary hover:text-text-primary hover:bg-bg-hover rounded-md transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-text-primary">
            {isEditMode ? "Edit" : "Create"}{" "}
            {layoutType === "test_run" ? "Test Run" : "Test Case"} Layout
          </h1>
          <p className="text-text-secondary mt-1">
            {isEditMode ? "Update" : "Configure"} your{" "}
            {layoutType === "test_run" ? "test run" : "test case"} layout
          </p>
        </div>
      </div>

      {/* Layout Name */}
      <div className="card p-6">
        <label className="block text-sm font-medium text-text-primary mb-2">
          Layout Name *
        </label>
        <Input
          type="text"
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
          placeholder="Enter layout name"
        />
        <p className="text-sm text-text-tertiary mt-1">
          This layout will be present in the 'Template' dropdown
        </p>
      </div>

      {/* Link Layout to Projects */}
      <div className="card p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-text-primary mb-1">
              Link Layout to Projects
            </h3>
            <p className="text-sm text-text-secondary">
              {selectedProjects.length}{" "}
              {selectedProjects.length === 1 ? "Project" : "Projects"} selected
            </p>
          </div>
          <button
            onClick={() => setShowProjectModal(true)}
            className="btn-primary px-4 py-2"
          >
            Link
          </button>
        </div>
      </div>

      {/* Organize Fields */}
      <div className="card p-6">
        <h3 className="text-lg font-medium text-text-primary mb-4">
          Organise these fields as per your preference by rearranging the fields
        </h3>

        <div className="grid grid-cols-2 gap-6">
          {/* Description Fields */}
          <div className="space-y-3">
            <h4 className="font-medium text-text-primary mb-2">
              Description Fields
            </h4>
            <div
              className="min-h-[200px] space-y-2"
              onDragOver={handleDragOver}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                // Calculate drop position based on mouse Y position
                const container = e.currentTarget;
                const rect = container.getBoundingClientRect();
                const y = e.clientY - rect.top;
                const items = Array.from(container.children);
                let dropIndex = items.length;

                for (let i = 0; i < items.length; i++) {
                  const itemRect = items[i].getBoundingClientRect();
                  const itemTop = itemRect.top - rect.top;
                  if (y < itemTop + itemRect.height / 2) {
                    dropIndex = i;
                    break;
                  }
                }
                handleDrop(e, "description", dropIndex);
              }}
            >
              {descriptionFields.map((field, index) => (
                <div
                  key={field.field_id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, field)}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDragOver(e);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDrop(e, "description", index);
                  }}
                  className="flex items-center space-x-2 p-3 bg-bg-primary border border-border-primary rounded-lg hover:border-primary-300 cursor-move"
                >
                  <GripVertical className="w-5 h-5 text-text-tertiary" />
                  <div className="flex-1">
                    <div className="font-medium text-sm">{field.name}</div>
                    <div className="text-xs text-text-tertiary">
                      Type: {field.type}
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedField(field);
                        }}
                      >
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMoveField("top", field);
                          setSelectedField(null);
                        }}
                      >
                        Move to top
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMoveField("up", field);
                          setSelectedField(null);
                        }}
                      >
                        Move up
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMoveField("down", field);
                          setSelectedField(null);
                        }}
                      >
                        Move down
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMoveField("bottom", field);
                          setSelectedField(null);
                        }}
                      >
                        Move to bottom
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveField(field);
                          setSelectedField(null);
                        }}
                        disabled={
                          field &&
                          field.name &&
                          DEFAULT_FIELDS.some((defaultName) =>
                            field.name
                              .toLowerCase()
                              .includes(defaultName.toLowerCase()),
                          )
                        }
                        className="text-red-600"
                      >
                        Remove Field
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ))}
            </div>
          </div>

          {/* Properties Fields */}
          <div className="space-y-3">
            <h4 className="font-medium text-text-primary mb-2">
              Properties Fields
            </h4>
            <div
              className="min-h-[200px] space-y-2"
              onDragOver={handleDragOver}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                // Calculate drop position based on mouse Y position
                const container = e.currentTarget;
                const rect = container.getBoundingClientRect();
                const y = e.clientY - rect.top;
                const items = Array.from(container.children);
                let dropIndex = items.length;

                for (let i = 0; i < items.length; i++) {
                  const itemRect = items[i].getBoundingClientRect();
                  const itemTop = itemRect.top - rect.top;
                  if (y < itemTop + itemRect.height / 2) {
                    dropIndex = i;
                    break;
                  }
                }
                handleDrop(e, "properties", dropIndex);
              }}
            >
              {propertiesFields.map((field, index) => (
                <div
                  key={field.field_id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, field)}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDragOver(e);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDrop(e, "properties", index);
                  }}
                  className="flex items-center space-x-2 p-3 bg-bg-primary border border-border-primary rounded-lg hover:border-primary-300 cursor-move"
                >
                  <GripVertical className="w-5 h-5 text-text-tertiary" />
                  <div className="flex-1">
                    <div className="font-medium text-sm">{field.name}</div>
                    <div className="text-xs text-text-tertiary">
                      Type: {field.type}
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedField(field);
                        }}
                      >
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMoveField("top", field);
                          setSelectedField(null);
                        }}
                      >
                        Move to top
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMoveField("up", field);
                          setSelectedField(null);
                        }}
                      >
                        Move up
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMoveField("down", field);
                          setSelectedField(null);
                        }}
                      >
                        Move down
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMoveField("bottom", field);
                          setSelectedField(null);
                        }}
                      >
                        Move to bottom
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveField(field);
                          setSelectedField(null);
                        }}
                        disabled={
                          field &&
                          field.name &&
                          DEFAULT_FIELDS.some((defaultName) =>
                            field.name
                              .toLowerCase()
                              .includes(defaultName.toLowerCase()),
                          )
                        }
                        className="text-red-600"
                      >
                        Remove Field
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Add New Fields */}
      <div className="card p-6">
        <h3 className="text-lg font-medium text-text-primary mb-4">
          Available Fields
        </h3>
        {availableFieldsForAdd.length > 0 ? (
          <div className="grid grid-cols-3 gap-3">
            {availableFieldsForAdd.map((field) => {
              // Double-check if field is already added (defensive check for UI)
              const isAlreadyAdded =
                descriptionFields.some(
                  (f) =>
                    f.field_id === field.field_id ||
                    (f.field_id &&
                      field.field_id &&
                      f.field_id.toString() === field.field_id.toString()),
                ) ||
                propertiesFields.some(
                  (f) =>
                    f.field_id === field.field_id ||
                    (f.field_id &&
                      field.field_id &&
                      f.field_id.toString() === field.field_id.toString()),
                );

              return (
                <button
                  key={field.field_id}
                  onClick={() => handleAddField(field)}
                  disabled={isAlreadyAdded}
                  className={`p-3 text-left border border-border-primary rounded-lg transition-colors ${
                    isAlreadyAdded
                      ? "opacity-50 cursor-not-allowed bg-gray-100"
                      : "hover:border-primary-300 hover:bg-primary-50 cursor-pointer"
                  }`}
                  title={
                    isAlreadyAdded
                      ? "This field is already added to the layout"
                      : `Add ${field.name} to layout`
                  }
                >
                  <div className="font-medium text-sm">{field.name}</div>
                  <div className="text-xs text-gray-500">
                    Type: {field.type}
                  </div>
                  {isAlreadyAdded && (
                    <div className="text-xs text-red-500 mt-1">
                      Already added
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
            <p className="text-text-secondary mb-2">No Form Fields Available</p>
            <p className="text-sm text-text-tertiary mb-4">
              There are no new form fields available for use at the moment.
              Create a field and then map it to your test case / test run
              layouts.
            </p>
            <button
              onClick={() => navigate("/settings?tab=fields")}
              className="btn-primary inline-flex items-center space-x-2"
            >
              <ExternalLink className="w-4 h-4" />
              <span>Create New Field</span>
            </button>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end space-x-3">
        <Button variant="outline" onClick={() => navigate("/settings?tab=0")}>
          Cancel
        </Button>
        <Button
          onClick={handleCreateTemplate}
          disabled={loading || loadingTemplate || !templateName.trim()}
        >
          {loading
            ? isEditMode
              ? "Updating..."
              : "Creating..."
            : isEditMode
              ? "Update Layout"
              : "Create Layout"}
        </Button>
      </div>

      {/* Field Menu - Now handled inline in DropdownMenu above */}

      {/* Project Selection Modal */}
      <Dialog open={showProjectModal} onOpenChange={setShowProjectModal}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Select Projects</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center space-x-2">
              <Switch
                id="apply-to-all-future"
                checked={applyToAllFuture}
                onCheckedChange={setApplyToAllFuture}
              />
              <Label htmlFor="apply-to-all-future" className="cursor-pointer">
                Apply to All Future Projects
              </Label>
            </div>
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {projects.map((project) => (
                <div
                  key={project.project_id}
                  className="flex items-center space-x-2 p-2 hover:bg-bg-hover rounded"
                >
                  <Checkbox
                    checked={selectedProjects.includes(project.project_id)}
                    onCheckedChange={() =>
                      handleProjectToggle(project.project_id)
                    }
                  />
                  <span className="flex-1 text-text-primary">
                    {project.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowProjectModal(false)}
            >
              Cancel
            </Button>
            <Button onClick={() => setShowProjectModal(false)}>Update</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreateTemplatePage;
